import React, { useState, useMemo } from "react";
import { useNotifications, Notification } from "../contexts/NotificationContext";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  Check,
  CheckCheck,
  CheckCircle,
  RotateCcw,
  Trash2,
  Info,
  AlertCircle,
  AlertTriangle,
  Clock,
} from "lucide-react";
import { WidgetErrorState } from "../components/Common/WidgetErrorState";
import { Skeleton } from "../components/Common/Skeleton";

type FilterTab = "all" | "unread" | "read";

const NotificationsPage: React.FC = () => {
  const {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    removeNotification,
    fetchNotifications,
  } = useNotifications();

  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<FilterTab>("all");

  const readCount = useMemo(
    () => Math.max(0, notifications.length - unreadCount),
    [notifications.length, unreadCount]
  );

  const filteredNotifications = useMemo(() => {
    if (activeTab === "unread") return notifications.filter((n) => !n.is_read);
    if (activeTab === "read") return notifications.filter((n) => n.is_read);
    return notifications;
  }, [notifications, activeTab]);

  const handleNotificationClick = (notif: Notification) => {
    if (!notif.is_read) {
      markAsRead(notif.id);
    }
    if (notif.link) {
      navigate(notif.link);
    }
  };

  const getTypeIcon = (type: Notification["type"], isUnread: boolean) => {
    const iconClass = isUnread ? "shrink-0" : "shrink-0 opacity-50";
    switch (type) {
      case "success":
        return <CheckCircle className={`text-emerald-500 ${iconClass}`} size={18} />;
      case "warning":
        return <AlertTriangle className={`text-amber-500 ${iconClass}`} size={18} />;
      case "error":
        return <AlertCircle className={`text-rose-500 ${iconClass}`} size={18} />;
      default:
        return <Info className={`text-[#15BBAE] ${iconClass}`} size={18} />;
    }
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#E9F7F5] text-[#15BBAE] flex items-center justify-center">
            <Bell size={22} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-[#10253A]">Notifications</h2>
            <p className="text-xs text-neutral-500 mt-0.5">
              Manage your updates and track unread notifications
            </p>
          </div>
        </div>

        {unreadCount > 0 && (
          <button
            onClick={() => markAllAsRead()}
            type="button"
            className="flex items-center gap-2 h-10 bg-white border border-neutral-200 text-neutral-700 hover:text-[#15BBAE] hover:border-[#15BBAE] px-4 rounded-lg text-sm font-medium shadow-xs hover:bg-[#F0FAF9] transition-all shrink-0 cursor-pointer"
          >
            <CheckCheck size={18} className="text-[#15BBAE]" />
            Mark all as read
          </button>
        )}
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center gap-2 border-b border-neutral-200 pb-1">
        <button
          type="button"
          onClick={() => setActiveTab("all")}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer flex items-center gap-2 ${
            activeTab === "all"
              ? "bg-[#E9F7F5] text-[#15BBAE] font-semibold"
              : "text-neutral-600 hover:bg-neutral-100"
          }`}
        >
          <span>All</span>
          <span
            className={`text-xs px-2 py-0.5 rounded-full ${
              activeTab === "all"
                ? "bg-[#15BBAE] text-white"
                : "bg-neutral-200 text-neutral-700"
            }`}
          >
            {notifications.length}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("unread")}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer flex items-center gap-2 ${
            activeTab === "unread"
              ? "bg-[#E9F7F5] text-[#15BBAE] font-semibold"
              : "text-neutral-600 hover:bg-neutral-100"
          }`}
        >
          <span>Unread</span>
          {unreadCount > 0 && (
            <span
              className={`text-xs px-2 py-0.5 rounded-full ${
                activeTab === "unread"
                  ? "bg-[#D96F91] text-white"
                  : "bg-[#D96F91]/20 text-[#D96F91]"
              }`}
            >
              {unreadCount}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("read")}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer flex items-center gap-2 ${
            activeTab === "read"
              ? "bg-[#E9F7F5] text-[#15BBAE] font-semibold"
              : "text-neutral-600 hover:bg-neutral-100"
          }`}
        >
          <span>Read</span>
          <span
            className={`text-xs px-2 py-0.5 rounded-full ${
              activeTab === "read"
                ? "bg-neutral-600 text-white"
                : "bg-neutral-200 text-neutral-700"
            }`}
          >
            {readCount}
          </span>
        </button>
      </div>

      {/* Notifications List */}
      <div className="bg-white rounded-xl shadow-xs border border-neutral-200 overflow-hidden">
        {loading && notifications.length === 0 ? (
          <div className="p-6 space-y-4" data-testid="notifications-loading">
            <Skeleton className="h-16 w-full rounded-lg" />
            <Skeleton className="h-16 w-full rounded-lg" />
            <Skeleton className="h-16 w-full rounded-lg" />
          </div>
        ) : error && notifications.length === 0 ? (
          <div className="p-6">
            <WidgetErrorState
              title="Unable to load notifications"
              error={error}
              onRetry={fetchNotifications}
            />
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="p-12 text-center flex flex-col items-center justify-center text-neutral-500">
            <div className="w-14 h-14 rounded-full bg-neutral-100 flex items-center justify-center mb-3 text-neutral-400">
              <Bell size={28} />
            </div>
            <p className="text-base font-semibold text-neutral-800 mb-1">
              {activeTab === "unread"
                ? "No unread notifications"
                : activeTab === "read"
                ? "No read notifications"
                : "No notifications"}
            </p>
            <p className="text-sm text-neutral-500">
              {activeTab === "unread"
                ? "You have reviewed all your notifications."
                : activeTab === "read"
                ? "Notifications you have read will appear here."
                : "You're completely caught up with your updates."}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-neutral-100">
            {filteredNotifications.map((notif) => {
              const isUnread = !notif.is_read;

              return (
                <div
                  key={notif.id}
                  data-testid={`notification-item-${notif.id}`}
                  onClick={() => handleNotificationClick(notif)}
                  className={`p-5 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 transition-all ${
                    notif.link ? "cursor-pointer" : ""
                  } ${
                    isUnread
                      ? "bg-[#F8FCFB] hover:bg-[#F0FAF9]"
                      : "bg-white hover:bg-neutral-50/70"
                  }`}
                >
                  <div className="flex items-start gap-3.5 flex-1 min-w-0">
                    <div className="mt-0.5">{getTypeIcon(notif.type, isUnread)}</div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {isUnread && (
                          <span
                            className="w-2 h-2 rounded-full bg-[#15BBAE] shrink-0"
                            title="Unread notification"
                            aria-label="Unread"
                          />
                        )}
                        <h4
                          className={`text-sm truncate ${
                            isUnread
                              ? "font-semibold text-neutral-900"
                              : "font-normal text-neutral-600"
                          }`}
                        >
                          {notif.title}
                        </h4>
                      </div>

                      <p
                        className={`text-sm break-words line-clamp-2 ${
                          isUnread ? "text-neutral-700 font-normal" : "text-neutral-400"
                        }`}
                      >
                        {notif.message}
                      </p>

                      <div className="flex items-center gap-1.5 text-xs text-neutral-400 mt-2">
                        <Clock size={12} />
                        <span>
                          {new Date(notif.createdAt).toLocaleString(undefined, {
                            dateStyle: "medium",
                            timeStyle: "short",
                          })}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1.5 shrink-0 self-end sm:self-center">
                    {isUnread ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          markAsRead(notif.id);
                        }}
                        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-neutral-600 hover:text-[#15BBAE] hover:bg-white border border-transparent hover:border-neutral-200 transition-all cursor-pointer"
                        title="Mark as read"
                      >
                        <Check size={15} className="text-[#15BBAE]" />
                        <span>Mark read</span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          markAsUnread(notif.id);
                        }}
                        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-neutral-500 hover:text-neutral-800 hover:bg-neutral-100 border border-transparent hover:border-neutral-200 transition-all cursor-pointer"
                        title="Mark as unread"
                      >
                        <RotateCcw size={14} />
                        <span>Mark unread</span>
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeNotification(notif.id);
                      }}
                      className="p-1.5 rounded-lg text-neutral-400 hover:bg-rose-50 hover:text-rose-600 transition-colors cursor-pointer"
                      title="Delete notification"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
